import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import { getStaticContent } from './universalDataFetchers';
import logger from '../common/logger';
import {
  COACHING_CATALOG_ACTIVE_FILTERS,
  EDC_SLUGS_COACHING_CATALOG,
  LEARN_LIVE_ENROLLMENT_HOST,
  LEARN_LIVE_SERVICES_HOST,
} from '../common/constants';
import {
  generateCatalogStateStringFromResult, getDefaultCoachingCatalogFilters, getEnvironmentConstant,
} from '../common/utilities';
import { getChildProperty } from '../common/object';

export const coachingCatalogSliceInitialState = {
  result: {},
  filterHistory: {},
  editorial: {},
  isLoaderVisible: null,
  hasError: false,
  errorMessage: null,
  fetchingFilters: null,
  filters: {},
};

export const getCoachingCatalogFilters = (state) => state.coachingCatalogData?.filters ?? {};

export const fetchCoachingCatalogData = createAsyncThunk(
  'coachingCatalogData',
  async ({
    filter, sort, env, page, pageSize, coachingType, userEntitlements = [], isPreview = false, // Added isPreview parameter
  }, { rejectWithValue, getState }) => {
    try {
      const filters = getCoachingCatalogFilters(getState());

      // Add preview query parameter if in preview mode
      const previewParam = isPreview ? '?preview=true' : '';
      const endpoint = `${getEnvironmentConstant({ env, key: LEARN_LIVE_SERVICES_HOST })}/cso/digitalcatalog/v1${previewParam}`;

      // For relevance sorting, convert to title_ASC and include user entitlements
      let effectiveSort = sort;
      let requestPayload = {
        ...filters,
        page,
        pageSize,
        coachingType,
        filter,
        sort: effectiveSort,
      };

      // If sorting by relevance, include user products and convert sort to title_ASC
      const isRelevanceSort = (Array.isArray(sort) && sort.includes('relevance')) || sort === 'relevance';

      if (isRelevanceSort) {
        effectiveSort = ['relevance'];
        const availableProducts = filters.products || [];

        const entitlementMap = new Map(
          userEntitlements.map((ent) => [ent.displayName, ent.active !== false]),
        );

        const allProductsWithEntitlementStatus = availableProducts.map((product) => ({
          id: product.id,
          label: product.label,
          active: entitlementMap.has(product.label) ? entitlementMap.get(product.label) : false,
        }));

        requestPayload = {
          ...requestPayload,
          sort: effectiveSort,
          products: allProductsWithEntitlementStatus,
        };
      }

      const coachingCatalogResponse = await axios.post(endpoint, requestPayload, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
      if (coachingCatalogResponse?.status !== 200) {
        throw new Error('Invalid response', coachingCatalogResponse);
      }
      // since we are prepending previous page(s) results, we need to update the total count
      if (page > 0) {
        coachingCatalogResponse.data.cards = [...getState().coachingCatalogData?.result?.cards, ...coachingCatalogResponse?.data?.cards];
      }
      return coachingCatalogResponse?.data;
    } catch (error) {
      logger.error('Error in fetchCoachingCatalogData', {
        error: error.message,
        stack: error.stack,
        env,
        filter,
        sort,
      });

      return rejectWithValue({
        error: error.message,
        status: 'Error',
      });
    }
  },
);

export const fetchCoachingEditorialData = createAsyncThunk(
  'coachingEditorialData',
  ({
    env, isPreview,
  }) => getStaticContent({
    env, isPreview, slugs: EDC_SLUGS_COACHING_CATALOG,
  }),
);

export const fetchCoachingCatalogFilters = createAsyncThunk(
  'fetchCoachingCatalogFilters',
  async ({ env }, { fulfillWithValue }) => {
    try {
      const optionsQueryParam = COACHING_CATALOG_ACTIVE_FILTERS.join(',');
      const endpoint = `${getEnvironmentConstant({ env, key: LEARN_LIVE_ENROLLMENT_HOST })}/coachingrequest/picklist/v1?options=${optionsQueryParam}`;
      const catalogFiltersResponse = await axios.get(endpoint, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (catalogFiltersResponse?.status !== 200) {
        throw new Error('Invalid response', catalogFiltersResponse);
      }
      return catalogFiltersResponse?.data;
    } catch (e) {
      logger.error('[coachingCatalogSlice] Error in fetching catalog filters ', e);
      return fulfillWithValue(getDefaultCoachingCatalogFilters());
    }
  },
);

export const coachingCatalogSlice = createSlice({
  name: 'coachingCatalogData',
  initialState: coachingCatalogSliceInitialState,
  reducers: {
    clearFilters: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.result.filters = draftState?.result?.filters?.map((filter) => ({ ...filter, selections: filter.selections.map((selection) => ({ ...selection, selected: false })) })) || [];
        draftState.result.filterGroups = draftState?.result?.filterGroups?.map((fg) => ({ ...fg, filters: fg.filters.map((filter) => ({ ...filter, selections: filter.selections.map((sel) => ({ ...sel, selected: false })) })) }));
        draftState.filterHistory = {};
      });
      return newState;
    },
    clearResults: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.result.cards = [];
        draftState.filterHistory = {};
        draftState.result.status = 'cleared';
        if (action?.payload && Object.keys(action.payload).includes('newTerm')) {
          draftState.result.term = action.payload.newTerm;
        }
        delete draftState.result.totalResults;
      });
      return newState;
    },
    clearError: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.hasError = false;
        draftState.errorMessage = null;
      });
      return newState;
    },
  },
  extraReducers: {
    [fetchCoachingEditorialData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.editorial = action?.payload;
      });
      return newState;
    },
    [fetchCoachingEditorialData.rejected]: (state, action) => {
      logger.error('Error in fetchCoachingEditorialData: ', action.error);
      return state;
    },

    [fetchCoachingCatalogData.pending]: (state) => ({
      ...state,
      isLoaderVisible: true,
      hasError: false,
      errorMessage: null,
    }),
    [fetchCoachingCatalogData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.isLoaderVisible = false;
        draftState.result = action?.payload ?? {};
        draftState.hasError = false;
        draftState.errorMessage = null;
        // Update filters based on payload
        COACHING_CATALOG_ACTIVE_FILTERS.forEach((filterKey) => {
          // {{filterKey}} from cso/digitalcatalog/v1 represents filter
          if (Object.hasOwn(action?.payload, filterKey)) {
            draftState.filters[filterKey] = action.payload[filterKey];
          }
        });
      });
      return newState;
    },
    [fetchCoachingCatalogData.rejected]: (state, action) => {
      logger.error('Error in fetchCoachingCatalogData: ', action.error);
      return {
        ...state,
        isLoaderVisible: false,
        hasError: true,
        errorMessage: action.payload?.error || action.error?.message || 'An error occurred while fetching coaching catalog data',
      };
    },
    [fetchCoachingCatalogFilters.pending]: (state) => produce(state, (draftState) => {
      draftState.fetchingFilters = true;
    }),
    [fetchCoachingCatalogFilters.fulfilled]: (state, action) => produce(state, (draftState) => {
      draftState.fetchingFilters = false;
      draftState.filters = action.payload;
    }),
    [fetchCoachingCatalogFilters.rejected]: (state) => produce(state, (draftState) => {
      draftState.fetchingFilters = false;
    }),
  },
});

// Export auto generated actions that match defined reducers
export const {
  clearResults, clearFilters, clearError,
} = coachingCatalogSlice.actions;

// Export getters for store data
export const getCoachingCatalogResult = (state) => state.coachingCatalogData.result;

export const getCoachingCatalogResultFilters = (state) => state.coachingCatalogData.result?.filters ?? [];

export const getCoachingFilterHistory = (state) => state.coachingCatalogData.filterHistory;

export const getCoachingStateString = (state, returnPage) => generateCatalogStateStringFromResult({ result: state.coachingCatalogData?.result, returnPage });

export const isCoachingLoaderVisible = (state) => state.coachingCatalogData.isLoaderVisible;

export const getCoachingEditorialData = (state) => getChildProperty(state, 'coachingCatalogData.editorial');

export const getCoachingCatalogError = (state) => ({
  hasError: state.coachingCatalogData?.hasError || false,
  errorMessage: state.coachingCatalogData?.errorMessage || null,
});

export const getFetchingCoachingCatalogFilters = (state) => state.coachingCatalogData?.fetchingFilters;

// Export reducer by default for import by store config
export default coachingCatalogSlice.reducer;
